import React from 'react';
import PropTypes from 'prop-types';

import { Route, Switch, Redirect } from 'react-router';
import queryString from 'query-string';

import universal from '@nerdwallet/coin/universal';

import Container from './container';

const FrontPage = universal(import('../containers/front-page'));
const PressKitRoute = universal(import('./press-kit-route'));
const SocialImpactRoute = universal(import('./socialimpact-route'));
const PressArchiveRoute = universal(import('./press-archive-route'));
const SocialImpactEmailRoute = universal(import('./socialimpact-email-route'));
const PressNewsRoute = universal(import('./press-news-route'));
const BenefitsRoute = universal(import('./benefits-route'));
const CompanyRoute = universal(import('./company-route'));
const LeadershipRoute = universal(import('./leadership-route'));
const CareersInfoRoute = universal(import('./careers-info-route'));
const CareersRoute = universal(import('./careers-route'));
const DepartmentRoute = universal(import('./department-route'));
const JobRoute = universal(import('./job-route'));
const PrivacyPolicyRoute = universal(import('./privacy-policy-route'));
const DiversityInclusionRoute = universal(
  import('./diversity-inclusion-route')
);

const appFrom = (Component) => (props) => {
  // `preloadWeak()` allows us to hoist all statics properly. If we don't
  // do this, the `pageImpression` analytics event will not get the
  // Components `analyticsName` static property, which breaks the event.
  // https://github.com/faceyspacey/react-universal-component#static-hoisting
  const InnerComponent = Component.preloadWeak();
  const newProps = { analyticsName: InnerComponent?.analyticsName, ...props };
  return (
    <Container {...newProps}>
      <Component />
    </Container>
  );
};

const WrappedRoutes = () => (
  <Switch>
    <Route
      exact
      path="/"
      render={(props) => {
        const App = appFrom(FrontPage);
        return <App {...props} />;
      }}
    />
    <Route path="/kit" component={appFrom(PressKitRoute)} />
    <Route path="/news" exact component={appFrom(PressNewsRoute)} />
    <Route path="/news/archive" component={appFrom(PressArchiveRoute)} />
    <Route path="/company" component={appFrom(CompanyRoute)} />
    <Route path="/leadership" component={appFrom(LeadershipRoute)} />
    <Route path="/benefits" component={appFrom(BenefitsRoute)} />
    <Route path="/socialimpact" exact component={appFrom(SocialImpactRoute)} />
    <Route
      path="/corporate-social-responsibility"
      exact
      component={appFrom(SocialImpactRoute)}
    />
    <Route
      path="/diversity-inclusion"
      exact
      component={appFrom(DiversityInclusionRoute)}
    />
    <Route
      path="/privacy-policy"
      exact
      component={appFrom(PrivacyPolicyRoute)}
    />
    <Route
      exact
      path="/corporate-social-responsibility/email"
      component={appFrom(SocialImpactEmailRoute)}
    />
    <Route path="/careers" exact component={appFrom(CareersInfoRoute)} />
    <Route path="/careers/teams" component={appFrom(CareersRoute)} />
    <Route
      path="/careers/job/:id"
      // if job URL contains *only* a greenhouse gh_src tracking id in the query string (ie: no gh_jid), add a gh_jid value to the redirect URL and then redirect to that built URL
      render={(props) => {
        const { location, match } = props;
        const oneGreenhouseSrcParam =
          location.search &&
          location.search.includes('gh_src=') &&
          !location.search.includes('gh_jid=');

        if (oneGreenhouseSrcParam) {
          const appendJobIdQueryString = {
            ...queryString.parse(location.search),
            gh_jid: match.params.id,
          };
          const jobIdAddedToRedirectUrl = {
            pathname: location.pathname,
            search: queryString.stringify(appendJobIdQueryString),
          };
          return <Redirect to={jobIdAddedToRedirectUrl} />;
        }
        const App = appFrom(JobRoute);
        return <App {...props} />;
      }}
    />
    <Route
      path="/careers/job*"
      render={(props) => {
        const { location } = props;
        const oneGreenhouseJidParam =
          location.search &&
          location.search.includes('gh_jid=') &&
          !location.search.includes('gh_src=');

        if (!oneGreenhouseJidParam) {
          return <Redirect to={{ pathname: '/careers' }} />;
        }
        const workingJobId = {
          ...queryString.parse(location.search),
        };
        return (
          <Redirect
            to={{
              pathname: `${location.pathname}/${workingJobId.gh_jid}`,
            }}
          />
        );
      }}
    />
    <Route path="/careers/:slug" component={appFrom(DepartmentRoute)} />
  </Switch>
);

WrappedRoutes.propTypes = {
  location: PropTypes.object,
  match: PropTypes.object,
  relativePath: PropTypes.object,
};

WrappedRoutes.displayName = 'Wrapped Routes';

export default WrappedRoutes;
